/**
 * Contact us — topic panels and Shopify contact form field visibility.
 */

/** @type {WeakMap<HTMLElement, AbortController>} */
const contactUsControllers = new WeakMap();

/** @param {HTMLElement} root */
function initContactUs(root) {
  contactUsControllers.get(root)?.abort();

  const abortController = new AbortController();
  const { signal } = abortController;
  contactUsControllers.set(root, abortController);

  const form = root.querySelector('[data-contact-us-form]');
  const select = root.querySelector('[data-contact-us-topic-select]');

  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  if (form.querySelector('[data-contact-us-success]')) {
    return;
  }

  if (select instanceof HTMLSelectElement) {
    initContactUsTopicPanels(root, form, select, signal);
  }
}

/**
 * @param {HTMLElement} root
 * @param {HTMLFormElement} form
 * @param {HTMLSelectElement} select
 * @param {AbortSignal} signal
 */
function initContactUsTopicPanels(root, form, select, signal) {
  const messagePanels = root.querySelectorAll('[data-contact-us-topic-message]');
  const inquiryPanels = root.querySelectorAll('[data-contact-us-topic-inquiries]');
  const formPanel = root.querySelector('[data-contact-us-topic-form]');
  const orderField = root.querySelector('[data-contact-us-order-field]');
  const liveRegion = root.querySelector('[data-contact-us-topic-live]');
  const formHeading = root.querySelector('#contact-form-heading');
  const formInputs = formPanel?.querySelectorAll('[data-contact-us-form-input]') ?? [];
  const submitButton = formPanel?.querySelector('[data-contact-us-submit]');

  /** @param {HTMLOptionElement | undefined} option */
  const applyTopicState = (option) => {
    const topicKey = option?.dataset.topicKey ?? '';
    const displayType = option?.dataset.displayType ?? '';
    const orderNumberMode = option?.dataset.orderNumberMode ?? 'hidden';
    const showForm = displayType === 'form';

    for (const panel of messagePanels) {
      if (!(panel instanceof HTMLElement)) {
        continue;
      }
      panel.hidden = panel.dataset.contactUsTopicMessage !== topicKey;
    }

    for (const panel of inquiryPanels) {
      if (!(panel instanceof HTMLElement)) {
        continue;
      }
      panel.hidden = panel.dataset.contactUsTopicInquiries !== topicKey;
    }

    if (formPanel instanceof HTMLElement) {
      formPanel.hidden = !showForm;
    }

    if (formHeading instanceof HTMLElement) {
      formHeading.hidden = displayType === 'inquiries';
    }

    if (showForm) {
      form.removeAttribute('novalidate');
    } else {
      form.setAttribute('novalidate', '');
    }

    for (const input of formInputs) {
      if (
        !(input instanceof HTMLInputElement) &&
        !(input instanceof HTMLTextAreaElement) &&
        !(input instanceof HTMLSelectElement)
      ) {
        continue;
      }

      input.disabled = !showForm;

      if (!showForm) {
        input.removeAttribute('required');
        input.removeAttribute('aria-required');
        input.setAttribute('aria-invalid', 'false');
        continue;
      }

      if (input.hasAttribute('data-contact-us-order-input')) {
        continue;
      }

      input.required = true;
      input.setAttribute('aria-required', 'true');
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !showForm;
    }

    if (orderField instanceof HTMLElement) {
      const orderInput = orderField.querySelector('[data-contact-us-order-input]');
      const label = orderField.querySelector('.dev-field__label');
      const showOrder = showForm && orderNumberMode !== 'hidden';

      orderField.hidden = !showOrder;

      if (orderInput instanceof HTMLInputElement) {
        orderInput.disabled = !showOrder;

        if (!showOrder) {
          orderInput.removeAttribute('required');
          orderInput.removeAttribute('aria-required');
          orderInput.value = '';
          orderInput.setAttribute('aria-invalid', 'false');

          if (label instanceof HTMLElement) {
            label.textContent = orderField.dataset.labelDefault ?? 'Order number';
          }
        } else if (orderNumberMode === 'required') {
          orderInput.required = true;
          orderInput.setAttribute('aria-required', 'true');

          if (label instanceof HTMLElement) {
            label.textContent = orderField.dataset.labelRequired ?? 'Order number';
          }
        } else {
          orderInput.removeAttribute('required');
          orderInput.removeAttribute('aria-required');

          if (label instanceof HTMLElement) {
            label.textContent = orderField.dataset.labelOptional ?? 'Order number';
          }
        }
      }
    }

    if (liveRegion instanceof HTMLElement) {
      if (displayType === 'message') {
        const activeMessage = root.querySelector(
          `[data-contact-us-topic-message="${CSS.escape(topicKey)}"]`
        );
        liveRegion.textContent = activeMessage?.textContent?.trim() ?? '';
      } else if (displayType === 'inquiries') {
        const activeInquiries = root.querySelector(
          `[data-contact-us-topic-inquiries="${CSS.escape(topicKey)}"]`
        );
        liveRegion.textContent = activeInquiries?.textContent?.trim() ?? '';
      } else {
        liveRegion.textContent = '';
      }
    }
  };

  const syncToSelectedTopic = () => {
    applyTopicState(select.selectedOptions[0]);
  };

  select.addEventListener('change', syncToSelectedTopic, { signal });

  if (!select.value) {
    const firstOption = Array.from(select.options).find((option) => option.value);

    if (firstOption) {
      select.value = firstOption.value;
    }
  }

  syncToSelectedTopic();

  window.addEventListener(
    'pageshow',
    () => {
      setTimeout(syncToSelectedTopic, 0);
    },
    { signal }
  );
}

/** @param {HTMLElement} root */
function destroyContactUs(root) {
  contactUsControllers.get(root)?.abort();
  contactUsControllers.delete(root);
}

function initAllContactUs() {
  for (const root of document.querySelectorAll('dev-help-center.dev-contact-us')) {
    if (root instanceof HTMLElement) {
      initContactUs(root);
    }
  }
}

function observeContactUs() {
  initAllContactUs();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) {
          continue;
        }

        if (node.matches('dev-help-center.dev-contact-us')) {
          initContactUs(node);
        } else {
          for (const root of node.querySelectorAll('dev-help-center.dev-contact-us')) {
            if (root instanceof HTMLElement) {
              initContactUs(root);
            }
          }
        }
      }

      for (const node of mutation.removedNodes) {
        if (!(node instanceof HTMLElement)) {
          continue;
        }

        if (node.matches('dev-help-center.dev-contact-us')) {
          destroyContactUs(node);
        } else {
          for (const root of node.querySelectorAll('dev-help-center.dev-contact-us')) {
            if (root instanceof HTMLElement) {
              destroyContactUs(root);
            }
          }
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeContactUs, { once: true });
} else {
  observeContactUs();
}
